
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { RegisterHeader } from "@/components/auth/RegisterHeader";
import { RegisterContent } from "@/components/auth/RegisterContent";
import { RegisterFeatures } from "@/components/auth/RegisterFeatures";
import { RegisterForm } from "@/components/auth/RegisterForm";
import { RoleSelect } from "@/components/auth/RoleSelect";
import { Loader2, Home } from "lucide-react";

const Register = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedRole, setSelectedRole] = useState("borrower");
  const [isLoading, setIsLoading] = useState(false);

  const handleRegister = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      // 1. Create the auth user with selected role
      const { data: { user }, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: selectedRole,
          },
        },
      });

      if (signUpError) throw signUpError;

      if (user) {
        // 2. Update the profile with the selected role and default employment_status
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ 
            role: selectedRole,
            employment_status: selectedRole === 'borrower' ? 'employed' : null, // Default for borrowers
          })
          .eq('id', user.id);

        if (profileError) {
          console.error('Error updating profile:', profileError);
          throw new Error('Failed to create user profile');
        }

        // 3. Create role-specific record if needed
        if (selectedRole === 'lender') {
          const { error: lenderError } = await supabase
            .from('lenders')
            .insert([
              { 
                user_id: user.id,
                institution_name: email.split('@')[0], // Default name from email
                status: 'pending'
              }
            ]);

          if (lenderError) {
            console.error('Error creating lender record:', lenderError);
            throw new Error('Failed to create lender record');
          }
        } else if (selectedRole === 'vendor') {
          const { error: vendorError } = await supabase
            .from('vendors')
            .insert([
              { 
                name: email.split('@')[0],
                status: 'pending',
                verification_status: 'pending'
              }
            ]);

          if (vendorError) {
            console.error('Error creating vendor record:', vendorError);
            throw new Error('Failed to create vendor record');
          }
        }

        toast({
          title: "Registration successful!",
          description: "Please check your email to verify your account.",
        });

        // 4. Redirect based on role
        switch (selectedRole) {
          case 'lender':
            navigate('/lender/signin');
            break;
          case 'vendor':
            navigate('/vendor/signin');
            break;
          default:
            navigate('/signin');
        }
      }
    } catch (error) {
      console.error("Error during registration:", error);
      toast({
        variant: "destructive",
        title: "Registration failed",
        description: error instanceof Error ? error.message : "Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container relative h-screen flex-col items-center justify-center grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      <div className="relative hidden h-full flex-col bg-muted p-10 text-white lg:flex dark:border-r">
        <div className="absolute inset-0 bg-primary" />
        <div className="relative z-20 flex items-center text-lg font-medium">
          <img src="/logo.svg" alt="Logo" className="h-8 w-8 mr-2" />
          <Link to="/" className="hover:underline">Obotan</Link>
        </div>
        <RegisterFeatures />
      </div>
      <div className="lg:p-8">
        <div className="absolute top-4 right-4 md:top-8 md:right-8">
          <Link
            to="/"
            className="p-2 hover:bg-muted rounded-full transition-colors flex items-center gap-1"
            aria-label="Go to home page"
          >
            <Home className="h-5 w-5" />
            <span className="text-sm">Home</span>
          </Link>
        </div>
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <RegisterHeader />
          <RegisterContent />
          <RoleSelect selectedRole={selectedRole} onRoleChange={setSelectedRole} />
          {isLoading ? (
            <div className="flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <RegisterForm onRegister={handleRegister} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
